import { useQuery } from "@apollo/client";
import Search from "@mui/icons-material/Search";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { ExtractedAddress, FacilityMatchSearchQuery } from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { FACILITY_SEARCH_QUERY } from "./api";

interface FacilityMatchDialogProps {
  open: boolean;
  onClose: () => void;
  extractedAddress: ExtractedAddress;
  tenantId: string;
}

type FacilityRow = FacilityMatchSearchQuery["facilities"]["items"][number];

function buildInitialDisplaySearch(address: ExtractedAddress): string {
  const parts: string[] = [];
  if (address.streetAddress) parts.push(address.streetAddress);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.zip) parts.push(address.zip);
  return parts.join(" ");
}

export function FacilityMatchDialog({
  open,
  onClose,
  extractedAddress,
  tenantId,
}: FacilityMatchDialogProps) {
  const initialDisplaySearch = buildInitialDisplaySearch(extractedAddress);
  const [searchInput, setSearchInput] = useState(initialDisplaySearch);
  const [debouncedSearch] = useDebounce(searchInput, 400);

  // Reset search input when dialog opens with new address data
  useEffect(() => {
    if (open) {
      setSearchInput(initialDisplaySearch);
    }
  }, [open, initialDisplaySearch]);

  const fullSearch = `tenantId:${tenantId} ${debouncedSearch}`.trim();

  const { data, loading } = useQuery(FACILITY_SEARCH_QUERY, {
    variables: { search: fullSearch, page: 0, pageSize: 25 },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const facilities = data?.facilities?.items ?? [];

  const columns: GridColDef<FacilityRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Facility Name",
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<FacilityRow>) => (
          <Box
            component={Link}
            to={`/o/${tenantId}/facilities/${params.row.id}/profile`}
            target="_blank"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {params.row.name}
          </Box>
        ),
      },
      { field: "streetAddress1", headerName: "Street Address", width: 200 },
      { field: "city", headerName: "City", width: 140 },
      { field: "state", headerName: "State", width: 80 },
      { field: "zip", headerName: "Zip", width: 100 },
      {
        field: "customerFacilityId",
        headerName: "Customer ID",
        width: 140,
      },
    ],
    [tenantId]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      title="Find Matching Facility"
      noPrimaryAction
      hideCancelButton
    >
      <Stack spacing={3}>
        {/* Extracted address summary */}
        <Card variant="outlined">
          <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Rejected Row Address Data
            </Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              {extractedAddress.name && <InfoChip label="Name" value={extractedAddress.name} />}
              {extractedAddress.streetAddress && (
                <InfoChip label="Street" value={extractedAddress.streetAddress} />
              )}
              {extractedAddress.city && <InfoChip label="City" value={extractedAddress.city} />}
              {extractedAddress.state && <InfoChip label="State" value={extractedAddress.state} />}
              {extractedAddress.zip && <InfoChip label="Zip" value={extractedAddress.zip} />}
              {extractedAddress.epaId && <InfoChip label="EPA ID" value={extractedAddress.epaId} />}
              {extractedAddress.vendorCode && (
                <InfoChip label="Vendor Code" value={extractedAddress.vendorCode} />
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Search input */}
        <TextField
          fullWidth
          size="small"
          label="Search facilities"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? <CircularProgress size={20} /> : <Search color="action" />}
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Results grid */}
        <Box sx={{ height: 400 }}>
          <DataGrid
            rows={facilities}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            pageSizeOptions={[25]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            noRowsMessage="No facilities found. Try adjusting the search."
          />
        </Box>

        {data?.facilities && data.facilities.count > facilities.length && (
          <Typography variant="caption" color="text.secondary">
            Showing {facilities.length} of {data.facilities.count.toLocaleString()} results
          </Typography>
        )}
      </Stack>
    </Dialog>
  );
}

function InfoChip({ label, value }: { label: string; value: string }) {
  return (
    <Typography variant="body2">
      <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        {label}:
      </Typography>
      <strong>{value}</strong>
    </Typography>
  );
}
